import { execAdminForm } from '../utils';

interface ValidateCatalogUploadVariables {
  partnerCode?: string;
  source?: string;
  validationId?: string;
  first?: number;
  after?: string | null;
}

export interface ValidationError {
  field: string;
  value: string;
  message: string;
  severity: 'error' | 'warning';
}

export interface PreviewRecord {
  rowNumber: number;
  action: 'create' | 'update';
  sku: string;
  values: Record<string, unknown>;
  existingValues: Record<string, unknown> | null;
  validationErrors: ValidationError[];
}

export interface PreviewRecordEdge {
  cursor: string;
  node: PreviewRecord;
}

export interface PageInfo {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor: string | null;
  endCursor: string | null;
}

export interface PreviewRecordsConnection {
  totalCount: number;
  edges: PreviewRecordEdge[];
  pageInfo: PageInfo;
}

export interface CatalogUploadValidation {
  validationId: string;
  valid: boolean;
  rowCount: number;
  columnCount: number;
  columns: string[];
  expectedCreates: number;
  expectedUpdates: number;
  errors: string[];
  hasValidationErrors?: boolean;
  totalValidationErrors?: number;
  expiresAt?: string;
  previewRecords?: PreviewRecordsConnection;
}

interface ValidateCatalogUploadResponse {
  validateCatalogUpload: CatalogUploadValidation;
}

const VALIDATE_CATALOG_UPLOAD_MUTATION = `
  mutation validateCatalogUpload(
    $partnerCode: String
    $source: String
    $validationId: String
    $first: Int
    $after: String
  ) {
    validateCatalogUpload(
      partnerCode: $partnerCode
      source: $source
      validationId: $validationId
      first: $first
      after: $after
    ) {
      validationId
      valid
      rowCount
      columnCount
      columns
      expectedCreates
      expectedUpdates
      errors
      hasValidationErrors
      totalValidationErrors
      expiresAt
      previewRecords {
        totalCount
        edges {
          cursor
          node {
            rowNumber
            action
            sku
            values
            existingValues
            validationErrors {
              field
              value
              message
              severity
            }
          }
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
      }
    }
  }
`;

export const validateCatalogUpload = async (
  variables: ValidateCatalogUploadVariables,
  file?: File,
): Promise<CatalogUploadValidation> => {
  const data = file ?
    await execAdminForm<ValidateCatalogUploadVariables, ValidateCatalogUploadResponse>(
      VALIDATE_CATALOG_UPLOAD_MUTATION,
      variables,
      { file },
    ) :
    await execAdminForm<ValidateCatalogUploadVariables, ValidateCatalogUploadResponse>(
      VALIDATE_CATALOG_UPLOAD_MUTATION,
      variables,
      {},
    );

  const result = data.validateCatalogUpload as CatalogUploadValidation & {
    columns: string | string[];
    previewRecords?: PreviewRecordsConnection & {
      edges: Array<PreviewRecordEdge & {
        node: PreviewRecord & {
          values: string | Record<string, unknown>;
          existingValues: string | Record<string, unknown> | null;
        };
      }>;
    };
  };

  // Parse columns if it comes back as a string instead of array
  if (typeof result.columns === 'string') {
    result.columns = result.columns.split(',').map((col: string) => col.trim()).filter((col: string) => col.length > 0);
  } else if (Array.isArray(result.columns) && result.columns.length === 1 && typeof result.columns[0] === 'string') {
    // Handle case where columns is an array with a single tab/space-separated string
    result.columns = result.columns[0].split(/\s+/).filter((col: string) => col.length > 0);
  }

  // Parse JSON fields in preview records if they come back as strings
  if (result.previewRecords?.edges) {
    result.previewRecords.edges = result.previewRecords.edges.map((edge) => {
      const parsedNode = { ...edge.node };

      // Parse values if it's a JSON string
      if (typeof parsedNode.values === 'string') {
        try {
          parsedNode.values = JSON.parse(parsedNode.values) as Record<string, unknown>;
        } catch {
          parsedNode.values = {};
        }
      }

      // Parse existingValues if it's a JSON string
      if (typeof parsedNode.existingValues === 'string') {
        try {
          parsedNode.existingValues = JSON.parse(parsedNode.existingValues) as Record<string, unknown>;
        } catch {
          parsedNode.existingValues = null;
        }
      }

      return {
        ...edge,
        node: parsedNode as PreviewRecord,
      };
    });
  }

  return result as CatalogUploadValidation;
};
